/**
 * SavingsSection — logs savings deposits for the current month.
 * Supports allocating deposits to named savings buckets (extraGoals).
 */
import React, { useState, useRef, useMemo } from 'react';
import { fc, fdate } from '../../utils';

export function SavingsSection({
  activeSavDeps,
  savingsBuckets,
  activeSavTot,
  savContrib,
  savDeposits,
  k,
  extraGoals,
  updateState,
  showToast,
  triggerConfetti,
}) {
  const [savGoalId, setSavGoalId] = useState(null);
  const [savAmt, setSavAmt] = useState('');
  const [showSplit, setShowSplit] = useState(false);
  const savRef = useRef();

  // Compute proportional split suggestion across goals that have a remaining target gap
  const splitSuggestion = useMemo(() => {
    const amt = parseFloat(savAmt) || 0;
    const goalsWithGap = (savingsBuckets || []).filter(
      (g) => g.target > 0 && (g.bal || 0) < g.target
    );
    if (amt <= 0 || goalsWithGap.length < 2) return null;
    const totalGap = goalsWithGap.reduce((s, g) => s + (g.target - (g.bal || 0)), 0);
    return goalsWithGap.map((g) => {
      const gap = g.target - (g.bal || 0);
      const share = Math.round((amt * (gap / totalGap)) * 100) / 100;
      return { goal: g, share };
    });
  }, [savAmt, savingsBuckets]);

  const handleAdd = (overrideAmt, overrideGoalId) => {
    const amt = parseFloat(overrideAmt ?? savRef.current?.value) || 0;
    const goalId = overrideGoalId !== undefined ? overrideGoalId : savGoalId;
    if (!amt || amt <= 0) { showToast('Enter an amount'); return; }
    const updated = { ...savDeposits };
    if (!Array.isArray(updated[k])) updated[k] = [];
    const entry = { amt, ts: Date.now(), ...(goalId ? { goalId } : {}) };
    updated[k] = [...updated[k], entry];
    const stateUpdate = { savDeposits: updated };
    if (goalId) {
      stateUpdate.extraGoals = (extraGoals || []).map((g) => {
        if (g.id !== goalId) return g;
        const newBal = Math.round(((g.bal || 0) + amt) * 100) / 100;
        if (g.target > 0 && newBal >= g.target && (g.bal || 0) < g.target) {
          triggerConfetti?.();
          showToast(`🎉 ${g.name} goal reached!`);
        }
        return { ...g, bal: newBal };
      });
    }
    updateState(stateUpdate);
    return stateUpdate; // used by applySplit to chain updates
  };

  const handleAddSingle = () => {
    if (!savAmt) { showToast('Enter an amount'); return; }
    handleAdd(savAmt, savGoalId);
    if (savRef.current) savRef.current.value = '';
    setSavAmt('');
    setShowSplit(false);
    showToast('Savings logged!');
  };

  const handleApplySplit = () => {
    if (!splitSuggestion) return;
    // Apply each portion as a separate deposit, chaining state updates
    // eslint-disable-next-line prefer-const
    let updatedDeps = { ...savDeposits };
    let updatedGoals = [...(extraGoals || [])];
    const now = Date.now();
    splitSuggestion.forEach(({ goal, share }, i) => {
      if (share <= 0) return;
      if (!Array.isArray(updatedDeps[k])) updatedDeps[k] = [];
      updatedDeps[k] = [...updatedDeps[k], { amt: share, ts: now + i, goalId: goal.id }];
      updatedGoals = updatedGoals.map((g) =>
        g.id === goal.id ? { ...g, bal: Math.round(((g.bal || 0) + share) * 100) / 100 } : g
      );
    });
    updateState({ savDeposits: updatedDeps, extraGoals: updatedGoals });
    if (savRef.current) savRef.current.value = '';
    setSavAmt('');
    setShowSplit(false);
    showToast('Split applied across goals!');
  };

  const handleDelete = (i) => {
    const deps = savDeposits[k] || [];
    const entry = deps[i];
    const updated = { ...savDeposits };
    updated[k] = deps.filter((_, idx) => idx !== i);
    const stateUpdate = { savDeposits: updated };
    if (entry?.goalId) {
      stateUpdate.extraGoals = (extraGoals || []).map((g) =>
        g.id === entry.goalId
          ? { ...g, bal: Math.max(0, Math.round(((g.bal || 0) - entry.amt) * 100) / 100) }
          : g
      );
    }
    updateState(stateUpdate);
  };

  return (
    <div className="card cp" style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
        💵 Savings This Month
      </div>

      {savingsBuckets.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 5 }}>Allocate to:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            <button onClick={() => setSavGoalId(null)} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: savGoalId === null ? 'var(--ylw)' : 'var(--bg)', color: savGoalId === null ? '#000' : 'var(--txt)', border: savGoalId === null ? '1.5px solid var(--ylw)' : '1.5px solid var(--bdr)' }}>
              General
            </button>
            {savingsBuckets.map((g) => (
              <button key={g.id} onClick={() => setSavGoalId(g.id)} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: savGoalId === g.id ? g.col || 'var(--ylw)' : 'var(--bg)', color: savGoalId === g.id ? '#000' : 'var(--txt)', border: savGoalId === g.id ? `1.5px solid ${g.col || 'var(--ylw)'}` : '1.5px solid var(--bdr)' }}>
                {g.emoji || '🎯'} {g.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {!activeSavDeps.length ? (
        <div className="em">
          No savings logged{savContrib > 0 ? ` yet — expected ${fc(savContrib)}/mo` : ' yet'}
        </div>
      ) : (
        activeSavDeps.map((s, i) => {
          const bucket = savingsBuckets.find((g) => g.id === s.goalId);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--bdr)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--grn)' }}>{fc(s.amt)}</div>
                <div style={{ fontSize: 10, color: 'var(--mut)' }}>
                  {fdate(new Date(s.ts))}{bucket ? ` · ${bucket.emoji || '🎯'} ${bucket.name}` : ''}
                </div>
              </div>
              <button className="del" onClick={() => handleDelete(i)}>✕</button>
            </div>
          );
        })
      )}

      {activeSavDeps.length > 0 && (
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--grn)', padding: '6px 0' }}>
          Total: {fc(activeSavTot)}
        </div>
      )}
      {/* Smart split suggestion */}
      {splitSuggestion && showSplit && (
        <div style={{ marginBottom: 10, padding: '10px 12px', background: 'rgba(107,203,119,.08)', border: '1px solid rgba(107,203,119,.25)', borderRadius: 9 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--grn)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>
            💡 Suggested Split
          </div>
          {splitSuggestion.map(({ goal, share }) => (
            <div key={goal.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
              <span style={{ color: 'var(--txt)' }}>{goal.emoji || '🎯'} {goal.name}</span>
              <span style={{ fontWeight: 700, color: goal.col || 'var(--grn)' }}>{fc(share)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button onClick={handleApplySplit} style={{ flex: 1, padding: '7px', borderRadius: 8, background: 'var(--grn)', color: '#fff', fontWeight: 700, fontSize: 12, border: 'none', cursor: 'pointer' }}>
              Apply Split
            </button>
            <button onClick={() => setShowSplit(false)} style={{ padding: '7px 10px', borderRadius: 8, background: 'none', color: 'var(--mut)', fontSize: 12, border: '1px solid var(--bdr)', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="pcw">
        <div className="pai">
          <span>$</span>
          <input
            type="number"
            ref={savRef}
            min="0"
            step="0.01"
            placeholder="0.00"
            value={savAmt}
            onChange={(e) => { setSavAmt(e.target.value); setShowSplit(false); }}
          />
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          {splitSuggestion && !showSplit && (
            <button onClick={() => setShowSplit(true)} title="Smart split across goals" style={{ padding: '0 10px', borderRadius: 8, border: '1.5px solid var(--grn)', background: 'rgba(107,203,119,.1)', color: 'var(--grn)', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              💡 Split
            </button>
          )}
          <button className="pab" onClick={handleAddSingle}>+ Log</button>
        </div>
      </div>
    </div>
  );
}
